import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Checkbox } from "./components/ui/checkbox";
import { toast } from "sonner";
import { 
  CreditCard, 
  User, 
  Home, 
  Smartphone, 
  Upload, 
  CheckCircle2, 
  Loader2,
  ArrowRight,
  ArrowLeft
} from "lucide-react";
import { translations, translateBackendError } from "./translations";
import { LanguageSwitcher } from "./components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getInitialLang = () => {
  const saved = localStorage.getItem("lang");
  return translations[saved] ? saved : "en";
};

function App() {
  const [lang, setLang] = useState(getInitialLang);
  const t = translations[lang];
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState("");
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    email: "",
    otpCode: "",
    firstName: "",
    lastName: "",
    street: "",
    houseNumber: "",
    zipCode: "",
    city: "",
    simNumber: "8932030000",
    pukCode: "",
    idImagePath: "",
    gdprConsent: false
  });

  const changeLang = (code) => {
    setLang(code);
    localStorage.setItem("lang", code);
    document.documentElement.lang = code;
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Check for payment return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    
    if (sessionId && currentStep === 1) {
      setPaymentSessionId(sessionId);
      pollPaymentStatus(sessionId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 30;
    
    if (attempts >= maxAttempts) {
      toast.error(t.errPaymentTimeout);
      return;
    }

    try {
      const response = await fetch(`${API}/payment/status/${sessionId}`);
      const data = await response.json();
      
      if (data.payment_status === "paid") {
        setPaymentCompleted(true);
        setCurrentStep(2);
        toast.success(t.okPaymentVerified);
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (data.status === "expired") {
        toast.error(t.errPaymentExpired);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      toast.error(t.errPaymentVerify);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${API}/payment/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: originUrl })
      });

      if (!response.ok) throw new Error("Failed to create payment session");

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(t.errPaymentInit);
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!formData.email) {
      toast.error(t.errEmailRequired);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, lang })
      });

      if (!response.ok) throw new Error("Failed to send OTP");

      toast.success(t.okCodeSent.replace("{email}", formData.email));
    } catch (error) {
      console.error("OTP error:", error);
      toast.error(t.errCodeSend);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!formData.otpCode) {
      toast.error(t.errCodeRequired);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: formData.email, 
          otp_code: formData.otpCode 
        })
      });

      if (!response.ok) throw new Error("Invalid OTP");

      toast.success(t.okEmailVerified);
      setCurrentStep(3);
    } catch (error) {
      console.error("OTP verification error:", error);
      toast.error(t.errCodeInvalid);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t.errFileType);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t.errFileSize);
      return;
    }

    setLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await fetch(`${API}/upload/id`, {
        method: "POST",
        body: formDataUpload
      });

      if (!response.ok) throw new Error("Failed to upload file");

      const data = await response.json();
      setFormData({ ...formData, idImagePath: data.file_path });
      toast.success(t.okFileUploaded);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(t.errFileUpload);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all fields
    if (!formData.firstName || !formData.lastName) {
      toast.error(t.errName);
      return;
    }
    if (!formData.street || !formData.houseNumber || !formData.zipCode || !formData.city) {
      toast.error(t.errAddress);
      return;
    }
    if (!formData.simNumber || !formData.simNumber.startsWith("8932")) {
      toast.error(t.errSimPrefix);
      return;
    }
    if (formData.simNumber.length !== 19) {
      toast.error(t.errSimLength);
      return;
    }
    if (!formData.pukCode || formData.pukCode.length !== 8) {
      toast.error(t.errPuk);
      return;
    }
    if (!formData.idImagePath) {
      toast.error(t.errIdRequired);
      return;
    }
    if (!formData.gdprConsent) {
      toast.error(t.errGdpr);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/registration/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          street: formData.street,
          house_number: formData.houseNumber,
          zip_code: formData.zipCode,
          city: formData.city,
          sim_number: formData.simNumber,
          puk_code: formData.pukCode,
          id_image_path: formData.idImagePath,
          payment_session_id: paymentSessionId,
          gdpr_consent: formData.gdprConsent,
          lang
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(translateBackendError(t, error.detail));
      }

      toast.success(t.okSubmitted);
      setCurrentStep(5);
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.message || t.errSubmit);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card className="p-8 max-w-md mx-auto" data-testid="payment-step">
            <div className="text-center mb-6">
              <CreditCard className="w-16 h-16 mx-auto mb-4 text-[#FF6600]" />
              <h2 className="text-2xl font-bold mb-2" data-testid="payment-title">{t.paymentTitle}</h2>
              <p className="text-gray-600" data-testid="payment-subtitle">
                {t.paymentSubtitle}
              </p>
            </div>
            <Button
              onClick={handlePayment}
              disabled={loading}
              className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white"
              data-testid="payment-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.processing}
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {t.payButton}
                </>
              )}
            </Button>
          </Card>
        );

      case 2:
        return (
          <Card className="p-8 max-w-md mx-auto" data-testid="otp-step">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_57aa1d34-59d5-45b0-a7a7-9b008f7bb482/artifacts/556mb1oa_Logo_klein_JanMobile_2%20%281%29.png" 
                  alt="Jan Mobile Logo" 
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t.otpTitle}</h2>
              <p className="text-gray-600">
                {t.otpSubtitle}
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">{t.emailLabel}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t.emailPlaceholder}
                  required
                  data-testid="email-input"
                />
              </div>
              <Button
                onClick={handleSendOTP}
                disabled={loading || !formData.email}
                className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white"
                data-testid="send-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.sending}
                  </>
                ) : (
                  t.sendCode
                )}
              </Button>
              
              <div className="pt-4 border-t">
                <Label htmlFor="otp">{t.codeLabel}</Label>
                <Input
                  id="otp"
                  value={formData.otpCode}
                  onChange={(e) => setFormData({ ...formData, otpCode: e.target.value })}
                  placeholder={t.codePlaceholder}
                  maxLength={6}
                  required
                  data-testid="otp-input"
                />
              </div>
              <Button
                onClick={handleVerifyOTP}
                disabled={loading || !formData.otpCode}
                className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white"
                data-testid="verify-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.verifying}
                  </>
                ) : (
                  <>
                    {t.verifyEmail}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </Card>
        );

      case 3:
        return (
          <Card className="p-8 max-w-2xl mx-auto" data-testid="customer-info-step">
            <div className="text-center mb-6">
              <User className="w-16 h-16 mx-auto mb-4 text-[#0066CC]" />
              <h2 className="text-2xl font-bold mb-2">{t.customerTitle}</h2>
              <p className="text-gray-600">{t.customerSubtitle}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">{t.firstName}</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder={t.firstNamePlaceholder}
                  required
                  data-testid="first-name-input"
                />
              </div>
              <div>
                <Label htmlFor="lastName">{t.lastName}</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder={t.lastNamePlaceholder}
                  required
                  data-testid="last-name-input"
                />
              </div>
              <div>
                <Label htmlFor="street">{t.street}</Label>
                <Input
                  id="street"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  placeholder={t.streetPlaceholder}
                  required
                  data-testid="street-input"
                />
              </div>
              <div>
                <Label htmlFor="houseNumber">{t.houseNumber}</Label>
                <Input
                  id="houseNumber"
                  value={formData.houseNumber}
                  onChange={(e) => setFormData({ ...formData, houseNumber: e.target.value })}
                  placeholder="123A"
                  required
                  data-testid="house-number-input"
                />
              </div>
              <div>
                <Label htmlFor="zipCode">{t.zipCode}</Label>
                <Input
                  id="zipCode"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  placeholder="1000"
                  required
                  data-testid="zip-code-input"
                />
              </div>
              <div>
                <Label htmlFor="city">{t.city}</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder={t.cityPlaceholder}
                  required
                  data-testid="city-input"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <Button
                onClick={() => setCurrentStep(2)}
                variant="outline"
                className="flex-1"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t.back}
              </Button>
              <Button
                onClick={() => setCurrentStep(4)}
                className="flex-1 bg-[#0066CC] hover:bg-[#0052A3] text-white"
                data-testid="next-button"
              >
                {t.next}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        );

      case 4:
        return (
          <Card className="p-8 max-w-2xl mx-auto" data-testid="sim-info-step">
            <div className="text-center mb-6">
              <Smartphone className="w-16 h-16 mx-auto mb-4 text-[#0066CC]" />
              <h2 className="text-2xl font-bold mb-2">{t.simTitle}</h2>
              <p className="text-gray-600">{t.simSubtitle}</p>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="simNumber">{t.simLabel}</Label>
                <Input
                  id="simNumber"
                  value={formData.simNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 19) {
                      setFormData({ ...formData, simNumber: value });
                    }
                  }}
                  placeholder="8932030000"
                  maxLength={19}
                  required
                  data-testid="sim-number-input"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.simNumber.length}/19 {t.digits}</p>
              </div>
              <div>
                <Label htmlFor="pukCode">{t.pukLabel}</Label>
                <Input
                  id="pukCode"
                  value={formData.pukCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 8) {
                      setFormData({ ...formData, pukCode: value });
                    }
                  }}
                  placeholder={t.pukPlaceholder}
                  maxLength={8}
                  required
                  data-testid="puk-code-input"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.pukCode.length}/8 {t.digits}</p>
              </div>
              
              <div className="pt-4 border-t">
                <Label htmlFor="idUpload">{t.uploadLabel}</Label>
                <div className="mt-2">
                  {formData.idImagePath ? (
                    <label htmlFor="idUpload" className="flex items-center justify-center w-full h-32 border-2 border-green-500 bg-green-50 rounded-lg cursor-pointer" data-testid="file-uploaded-area">
                      <div className="text-center">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                        <p className="text-lg font-bold text-green-600">{t.fileUploaded}</p>
                        <p className="text-sm text-gray-600 mt-1">{t.clickToChange}</p>
                      </div>
                    </label>
                  ) : (
                    <label
                      htmlFor="idUpload"
                      className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#FF6600] transition-colors"
                      data-testid="file-upload-area"
                    >
                      <div className="text-center">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-600">{t.clickToUpload}</p>
                      </div>
                    </label>
                  )}
                  <input
                    id="idUpload"
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    required
                    data-testid="file-input"
                  />
                </div>
              </div>

              <div className="flex items-start space-x-2 pt-4">
                <Checkbox
                  id="gdpr"
                  checked={formData.gdprConsent}
                  onCheckedChange={(checked) => setFormData({ ...formData, gdprConsent: checked })}
                  required
                  data-testid="gdpr-checkbox"
                />
                <Label htmlFor="gdpr" className="text-sm leading-relaxed cursor-pointer">
                  {t.gdpr}
                </Label>
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <Button
                onClick={() => setCurrentStep(3)}
                variant="outline"
                className="flex-1"
                data-testid="back-button-sim"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t.back}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-[#FF6600] hover:bg-[#E55A00] text-white"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.submitting}
                  </>
                ) : (
                  t.submit
                )}
              </Button>
            </div>
          </Card>
        );

      case 5:
        return (
          <Card className="p-8 max-w-md mx-auto text-center" data-testid="success-step">
            <CheckCircle2 className="w-24 h-24 mx-auto mb-4 text-green-500" />
            <h2 className="text-3xl font-bold mb-4">{t.successTitle}</h2>
            <p className="text-gray-600 mb-6">
              {t.successSubtitle}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-800">
                <strong>{t.whatsNext}</strong><br />
                {t.successInfo}<br /><br />
                {t.successContact} <strong>+32 486 999 889</strong> {t.orEmail} <strong>cs@janmobile.be</strong>
              </p>
            </div>
            <Button
              onClick={() => window.location.href = 'https://janmobile.be'}
              className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white"
              data-testid="back-to-home-button"
            >
              <Home className="w-4 h-4 mr-2" />
              {t.backHome}
            </Button>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4">
            <a 
              href="https://janmobile.be" 
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
              data-testid="logo-link"
            >
              <div className="h-10 px-2 bg-[#FF6600] rounded-lg flex items-center justify-center" data-testid="header-logo">
                <img
                  src="/jan-mobile-logo-white.png"
                  alt="Jan Mobile"
                  className="h-7 w-auto object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Jan Mobile</h1>
                <p className="text-xs text-gray-500" data-testid="header-subtitle">{t.headerSubtitle}</p>
              </div>
            </a>
            
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Progress indicator */}
              {currentStep < 5 && (
                <div className="hidden sm:flex items-center space-x-2" data-testid="progress-indicator">
                  {[1, 2, 3, 4].map((step) => (
                    <div
                      key={step}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        step < currentStep
                          ? "bg-green-500 text-white"
                          : step === currentStep
                          ? "bg-[#0066CC] text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {step < currentStep ? "✓" : step}
                    </div>
                  ))}
                </div>
              )}
              <LanguageSwitcher lang={lang} onChange={changeLang} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500" data-testid="footer-text">
            {t.footer}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
